import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { ExplorePlaylistKind } from "#shared/schemas/playlistExplore.ts";
import { exploreSeedKey } from "#shared/schemas/playlistExplore.ts";

export interface ExplorePinnedPos {
  x: number;
  y: number;
}

/**
 * One expansion event: parent opened and introduced children.
 * Collapse removes nodes whose only remaining introducer is this expansion.
 */
export interface ExploreExpansion {
  /** Stable id — usually the parent node key (one live expansion per node). */
  id: string;
  parentKey: string;
  introducedKeys: string[];
  /** How many children this expansion has already paged in. */
  loaded: number;
  total: number;
}

export interface ExploreSession {
  seedKind: ExplorePlaylistKind;
  seedId: string;
  seedLabel: string;
  /** Node keys present on the canvas (includes seed). */
  nodeKeys: string[];
  /** Edge keys `artistId|kind:playlistId`. */
  edgeKeys: string[];
  expansions: ExploreExpansion[];
  /** Expansion order oldest→newest — collapse warning uses this. */
  expansionOrder: string[];
  pinned: Record<string, ExplorePinnedPos>;
  /**
   * Lightweight display cache so reopen does not flash empty labels before
   * the next fetch. Not a query cache — expand still re-queries.
   */
  nodeMeta: Record<
    string,
    {
      kind: "artist" | ExplorePlaylistKind;
      id: string;
      name: string | null;
      image_url: string | null;
      track_count: number;
    }
  >;
  edgeMeta: Record<string, { artist_id: string; playlist_kind: ExplorePlaylistKind; playlist_id: string; track_count: number }>;
}

interface PlaylistExploreState {
  sessions: Record<string, ExploreSession>;
  getSession: (kind: ExplorePlaylistKind, id: string) => ExploreSession | null;
  upsertSession: (session: ExploreSession) => void;
  clearSession: (kind: ExplorePlaylistKind, id: string) => void;
  setPinned: (kind: ExplorePlaylistKind, id: string, nodeKey: string, pos: ExplorePinnedPos | null) => void;
}

function emptySession(
  kind: ExplorePlaylistKind,
  id: string,
  label: string,
): ExploreSession {
  const seedKey = exploreSeedKey(kind, id);
  return {
    seedKind: kind,
    seedId: id,
    seedLabel: label,
    nodeKeys: [seedKey],
    edgeKeys: [],
    expansions: [],
    expansionOrder: [],
    pinned: {},
    nodeMeta: {
      [seedKey]: {
        kind,
        id,
        name: label,
        image_url: null,
        track_count: 0,
      },
    },
    edgeMeta: {},
  };
}

export const usePlaylistExploreStore = create<PlaylistExploreState>()(
  persist(
    (set, get) => ({
      sessions: {},

      getSession: (kind, id) => get().sessions[exploreSeedKey(kind, id)] ?? null,

      upsertSession: (session) =>
        set((state) => ({
          sessions: {
            ...state.sessions,
            [exploreSeedKey(session.seedKind, session.seedId)]: session,
          },
        })),

      clearSession: (kind, id) =>
        set((state) => {
          const key = exploreSeedKey(kind, id);
          const { [key]: _removed, ...rest } = state.sessions;
          return { sessions: rest };
        }),

      setPinned: (kind, id, nodeKey, pos) =>
        set((state) => {
          const key = exploreSeedKey(kind, id);
          const session = state.sessions[key];
          if (!session) return state;
          const pinned = { ...session.pinned };
          if (pos === null) delete pinned[nodeKey];
          else pinned[nodeKey] = pos;
          return {
            sessions: {
              ...state.sessions,
              [key]: { ...session, pinned },
            },
          };
        }),
    }),
    {
      name: "coykoi-playlist-explore",
      partialize: (state) => ({ sessions: state.sessions }),
    },
  ),
);

export { emptySession };
